import { ProConfigProvider } from '@ant-design/pro-components';
import { useTheme } from '@zhiyue/core';
import { App, ConfigProvider, type ThemeConfig, theme } from 'antd';
import React from 'react';
import { AppMessageBridge } from '@/utils/appMessage';

export const getThemeAlgorithm: (dark: boolean) => ThemeConfig['algorithm'] = (
  dark: boolean,
) => (dark ? theme.darkAlgorithm : theme.defaultAlgorithm);

export const getNavigationToken = (dark: boolean) => {
  const background = dark ? '#1E1E1E' : '#F0F0F3';
  const text = dark ? 'rgba(255,255,255,0.88)' : '#1F2633';
  const muted = dark ? 'rgba(255,255,255,0.65)' : '#596579';
  const hover = dark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)';
  const selected = dark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.06)';
  return {
    header: {
      heightLayoutHeader: 44,
      colorBgHeader: background,
      colorBgScrollHeader: background,
      colorHeaderTitle: text,
      colorTextMenu: muted,
      colorTextMenuSelected: text,
      colorTextMenuActive: text,
      colorTextRightActionsItem: text,
      colorBgMenuItemSelected: 'transparent',
      colorBgMenuItemHover: 'transparent',
    },
    sider: {
      colorMenuBackground: background,
      colorTextMenu: muted,
      colorTextMenuSelected: text,
      colorTextMenuActive: text,
      colorTextSubMenuSelected: text,
      colorTextMenuItemHover: text,
      colorBgMenuItemHover: hover,
      colorBgMenuItemSelected: selected,
      colorTextCollapsedButton: muted,
      colorTextCollapsedButtonHover: text,
    },
  };
};

export function getApplicationTheme(dark: boolean): ThemeConfig {
  return {
    inherit: false,
    algorithm: getThemeAlgorithm(dark),
    cssVar: { key: `crm-${dark ? 'dark' : 'light'}` },
    components: {
      Button: {
        defaultBg: dark ? '#2b2b2b' : '#eeeeee',
        defaultHoverBg: dark ? '#363636' : '#e2e2e2',
        defaultActiveBg: dark ? '#414141' : '#d6d6d6',
        defaultHoverColor: dark ? 'rgba(255,255,255,.85)' : '#1f1f1f',
        defaultActiveColor: dark ? 'rgba(255,255,255,.85)' : '#1f1f1f',
        defaultGhostColor: dark ? 'rgba(255,255,255,.85)' : '#1f1f1f',
        defaultGhostBorderColor: 'transparent',
        defaultBorderColor: 'transparent',
        defaultHoverBorderColor: 'transparent',
        defaultActiveBorderColor: 'transparent',
      },
      Menu: { colorPrimary: dark ? '#69b1ff' : '#0958d9' },
      Tabs: { colorPrimary: dark ? '#69b1ff' : '#0958d9' },
      Pagination: { colorPrimary: dark ? '#69b1ff' : '#0958d9' },
    },
    token: {
      colorPrimary: '#0958d9',
      colorPrimaryText: dark ? '#69b1ff' : '#0958d9',
      colorPrimaryTextHover: dark ? '#91caff' : '#003eb3',
      colorLink: dark ? '#69b1ff' : '#0958d9',
      colorLinkHover: dark ? '#91caff' : '#003eb3',
      colorLinkActive: dark ? '#91caff' : '#003eb3',
      colorBorder: dark ? '#6b6b6b' : '#8c8c8c',
      colorFillAlter: dark ? '#1d1d1d' : '#fafafa',
      colorFillTertiary: dark ? '#242424' : '#f5f5f5',
      colorTextSecondary: dark ? 'rgba(255,255,255,.68)' : '#596579',
      colorTextTertiary: dark ? 'rgba(255,255,255,.65)' : '#596579',
      colorTextPlaceholder: dark ? '#999999' : '#596579',
      colorSuccessText: dark ? '#95de64' : '#237804',
      colorWarningText: dark ? '#ffd666' : '#874d00',
      colorErrorText: dark ? '#ff7875' : '#a8071a',
      fontFamily: 'AlibabaSans, sans-serif',
      colorBgLayout: dark ? '#121212' : '#FFFFFF',
      borderRadius: 8,
      controlHeight: 32,
    },
  };
}

export default function PlatformThemeBoundary({
  children,
}: React.PropsWithChildren) {
  const { navTheme } = useTheme();
  const dark = navTheme === 'realDark';

  React.useEffect(() => {
    ConfigProvider.config({
      holderRender: (node) => (
        <ConfigProvider theme={getApplicationTheme(dark)}>
          {node}
        </ConfigProvider>
      ),
    });
  }, [dark]);

  return (
    <ConfigProvider theme={getApplicationTheme(dark)}>
      <ProConfigProvider dark={dark}>
        <App className="crm-content-theme">
          <AppMessageBridge />
          {children}
        </App>
      </ProConfigProvider>
    </ConfigProvider>
  );
}

export function useResolvedThemeToken() {
  const { navTheme } = useTheme();
  return React.useMemo(
    () => ({
      token: theme.getDesignToken(getApplicationTheme(navTheme === 'realDark')),
    }),
    [navTheme],
  );
}
